import { useMutation } from '@apollo/client'
import { FormikHelpers } from 'formik'
import { groupBy, isEmpty, keys } from 'lodash'
import { ReactElement, useEffect, useState } from 'react'
import { useIntl, defineMessages, FormattedMessage } from 'react-intl'

import FormLayout from 'components/v2/FormLayout'
import Heading from 'components/v2/Heading'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { useLogin } from 'features/Login/hooks'
import { NewPartnerCost } from 'features/PartnerCost/components/NewPartnerCost'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { canWrite } from 'features/Users/utils'
import { INewPartnerCostFormValues } from 'features/PartnerCost/types'
import { UPSERT_PARTNER_COSTS } from 'features/PartnerCost/mutations'
import { mapFormValuesToUpsertPartnerCostsInput } from 'features/PartnerCost/utils'
import { SuccessNotification } from 'features/PartnerCost/components/SuccessNotification'
import { CostType } from 'gql/generated/graphql'

const messages = defineMessages({
  helmet: {
    id: 'partnerCost.new.helmet',
    defaultMessage: 'New Partner Cost | Nexbus Admin'
  },
  upsertPartnerCostsError: {
    id: 'partnerCost.new.upsertPartnerCostsError',
    defaultMessage: 'Error upserting partner cost for vehicles with capacity {capacity}'
  }
})

const AssignCostPerKmView = (): ReactElement => {
  const intl = useIntl()
  const login = useLogin()
  const [isLoading, setLoading] = useState(false)
  const [errorsMessages, setErrorsMessages] = useState<string[]>([])
  const [isShowingSuccessNotification, setIsShowingSuccessNotification] = useState(false)

  const canCreatePartnerCost = canWrite(FEATURES.PARTNER_COST, login.accessToken.policies)

  const [upsertPartnerCosts] = useMutation(UPSERT_PARTNER_COSTS)
  useEffect((): void => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  if (!canCreatePartnerCost) {
    return <ForbiddenView feature={FEATURES.PARTNER_COST} />
  }

  const handleSubmit = async (
    values: INewPartnerCostFormValues,
    { resetForm }: FormikHelpers<INewPartnerCostFormValues>
  ): Promise<void> => {
    setLoading(true)
    setErrorsMessages([])
    const groupedVehicles = groupBy(values.partnerVehicles, 'capacity')

    try {
      const vehicleCapacitiesList = keys(groupedVehicles)
      const allUpsertPartnerCosts = vehicleCapacitiesList.map((vehicleCapacity) => {
        const input = mapFormValuesToUpsertPartnerCostsInput(values, parseInt(vehicleCapacity))
        return upsertPartnerCosts({
          variables: {
            input: {
              ...input,
              schedule: {
                ...input.schedule,
                type: CostType.PerKm
              }
            }
          }
        })
      })
      const allUpsertPartnerCostsResults = await Promise.allSettled(allUpsertPartnerCosts)
      const vehicleCapacitiesWithResult = vehicleCapacitiesList.map((capacity, index) => ({
        capacity,
        ...allUpsertPartnerCostsResults[index]
      }))
      const notChangedVehicleCapacities = vehicleCapacitiesWithResult.filter(({ status }) => status === 'rejected')
      setLoading(false)
      if (!isEmpty(notChangedVehicleCapacities)) {
        setErrorsMessages(
          notChangedVehicleCapacities.map(({ capacity }): string => {
            return intl.formatMessage(messages.upsertPartnerCostsError, {
              capacity
            })
          })
        )
      } else {
        setIsShowingSuccessNotification(true)
        resetForm()
      }
    } catch (error) {
      setLoading(false)
      // Do nothing
    }
  }

  return (
    <FormLayout.Container>
      <FormLayout.Content>
        <Heading.H3>
          <FormattedMessage id="partnerCost.assignCostPerKm.title" defaultMessage="Cost per Km" />
        </Heading.H3>
        <NewPartnerCost onSubmit={handleSubmit} loading={isLoading} errors={errorsMessages} isPerKm />
      </FormLayout.Content>
      {isShowingSuccessNotification && (
        <SuccessNotification onClose={(): void => setIsShowingSuccessNotification(false)} />
      )}
    </FormLayout.Container>
  )
}

export default withLastUrl(AssignCostPerKmView)
